import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import LandingPage from './pages/LandingPage';
import ContactForm from './pages/ContactForm';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import MemberTrackerBoard from './pages/MemberTrackerBoard';
import MediaProductionBoards from './pages/MediaProductionBoards';
import BoardView from './pages/BoardView';
import EventsCalendar from './pages/EventsCalendar';
import UsersManagement from './pages/UsersManagement';
import LandingPageCMS from './pages/LandingPageCMS';
import ManageWorkflow from './pages/ManageWorkflow';
import ManageMediaWorkflow from './pages/ManageMediaWorkflow';
import CustomerUploadPortal from './pages/CustomerUploadPortal';
import SpeakerUploadPortal from './pages/SpeakerUploadPortal';
import SupportRequestForm from './pages/SupportRequestForm';
import ManageSupportRequests from './pages/ManageSupportRequests';
import NurturingList from './pages/NurturingList';
import ContactsList from './pages/ContactsList';
import ProposalsList from './pages/ProposalsList';
import ProposalEditor from './pages/ProposalEditor';
import ProposalView from './pages/ProposalView';
import ProposalTemplates from './pages/ProposalTemplates';
import TemplateEditor from './pages/TemplateEditor';
import TemplatePreview from './pages/TemplatePreview';
import SectionTemplatesManager from './pages/SectionTemplatesManager';
import BulkProposalSender from './pages/BulkProposalSender';
import ProductsManagement from './pages/ProductsManagement';
import PublicProposal from './pages/PublicProposal';
import BacklogManagement from './pages/BacklogManagement';
import AdminSandbox from './pages/AdminSandbox';
import BrevoSettings from './pages/BrevoSettings';
import Settings from './pages/Settings';
import GmailAdminSettings from './pages/GmailAdminSettings';
import SystemEmailTemplates from './pages/SystemEmailTemplates';
import SystemEmailEditor from './pages/SystemEmailEditor';
import TasksPage from './pages/TasksPage';
import IntelligencePage from './pages/IntelligencePage';
import ContractsPayments from './pages/ContractsPayments';
import StripeAdmin from './pages/StripeAdmin';
import PaymentSetup from './pages/PaymentSetup';
import DataImport from './pages/DataImport';
import Goals from './pages/Goals';
import Documentation from './pages/Documentation';
import HelpFilesAdmin from './pages/HelpFilesAdmin';
import N8NIntegration from './pages/N8NIntegration';
import PressReleaseWriter from './pages/PressReleaseWriter';
import PressReleaseView from './pages/PressReleaseView';
import TemplateBuilder from './pages/TemplateBuilder';
import ProspectingDashboard from './pages/prospecting/ProspectingDashboard';
import ICPManager from './pages/prospecting/ICPManager';
import LeadPipeline from './pages/prospecting/LeadPipeline';
import LeadDetail from './pages/prospecting/LeadDetail';
import ImportLeads from './pages/prospecting/ImportLeads';
import ProspectingAdmin from './pages/prospecting/ProspectingAdmin';
import ScrapingJobs from './pages/prospecting/ScrapingJobs';
import Sequences from './pages/prospecting/Sequences';
import SequenceEditor from './pages/prospecting/SequenceEditor';
import MessageTemplates from './pages/prospecting/MessageTemplates';
import Analytics from './pages/prospecting/Analytics';
import ExtensionInstall from './pages/prospecting/ExtensionInstall';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/contact" element={<ContactForm />} />
      <Route path="/upload/:token" element={<CustomerUploadPortal />} />
      <Route path="/speaker-upload/:token" element={<SpeakerUploadPortal />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/member-tracker" element={<ProtectedRoute><MemberTrackerBoard /></ProtectedRoute>} />
      <Route path="/media-production" element={<ProtectedRoute><MediaProductionBoards /></ProtectedRoute>} />
      <Route path="/board/:boardId" element={<ProtectedRoute><BoardView /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><EventsCalendar /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UsersManagement /></ProtectedRoute>} />
      <Route path="/landing-cms" element={<ProtectedRoute><LandingPageCMS /></ProtectedRoute>} />
      <Route path="/manage-workflow" element={<ProtectedRoute><ManageWorkflow /></ProtectedRoute>} />
      <Route path="/manage-media-workflow" element={<ProtectedRoute><ManageMediaWorkflow /></ProtectedRoute>} />
      <Route path="/support-request" element={<ProtectedRoute><SupportRequestForm /></ProtectedRoute>} />
      <Route path="/manage-requests" element={<ProtectedRoute><ManageSupportRequests /></ProtectedRoute>} />
      <Route path="/nurturing" element={<ProtectedRoute><NurturingList /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><ContactsList /></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute><ProposalsList /></ProtectedRoute>} />
      <Route path="/proposals/templates" element={<ProtectedRoute><ProposalTemplates /></ProtectedRoute>} />
      <Route path="/proposals/templates/:templateId/edit" element={<ProtectedRoute><TemplateEditor /></ProtectedRoute>} />
      <Route path="/proposals/templates/:templateId/preview" element={<ProtectedRoute><TemplatePreview /></ProtectedRoute>} />
      <Route path="/proposals/builder/:templateId" element={<ProtectedRoute><TemplateBuilder /></ProtectedRoute>} />
      <Route path="/proposals/builder/new" element={<ProtectedRoute><TemplateBuilder /></ProtectedRoute>} />
      <Route path="/proposals/sections" element={<ProtectedRoute><SectionTemplatesManager /></ProtectedRoute>} />
      <Route path="/proposals/bulk-send" element={<ProtectedRoute><BulkProposalSender /></ProtectedRoute>} />
      <Route path="/proposals/:proposalId/edit" element={<ProtectedRoute><ProposalEditor /></ProtectedRoute>} />
      <Route path="/proposals/:proposalId" element={<ProtectedRoute><ProposalView /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute><ProductsManagement /></ProtectedRoute>} />
      <Route path="/admin/backlog" element={<ProtectedRoute><BacklogManagement /></ProtectedRoute>} />
      <Route path="/admin/sandbox" element={<ProtectedRoute><AdminSandbox /></ProtectedRoute>} />
      <Route path="/admin/brevo" element={<ProtectedRoute><BrevoSettings /></ProtectedRoute>} />
      <Route path="/admin/gmail" element={<ProtectedRoute><GmailAdminSettings /></ProtectedRoute>} />
      <Route path="/admin/system-emails" element={<ProtectedRoute><SystemEmailTemplates /></ProtectedRoute>} />
      <Route path="/admin/system-emails/:templateId/edit" element={<ProtectedRoute><SystemEmailEditor /></ProtectedRoute>} />
      <Route path="/admin/help-videos" element={<ProtectedRoute><HelpFilesAdmin /></ProtectedRoute>} />
      <Route path="/admin/n8n" element={<ProtectedRoute><N8NIntegration /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/tasks/:view" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/intelligence" element={<ProtectedRoute><IntelligencePage /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><ContractsPayments /></ProtectedRoute>} />
      <Route path="/admin/stripe" element={<ProtectedRoute><StripeAdmin /></ProtectedRoute>} />
      <Route path="/admin/data-import" element={<ProtectedRoute><DataImport /></ProtectedRoute>} />
      <Route path="/documentation" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />
      <Route path="/press-releases" element={<ProtectedRoute><PressReleaseWriter /></ProtectedRoute>} />
      <Route path="/press-releases/:pressReleaseId" element={<ProtectedRoute><PressReleaseView /></ProtectedRoute>} />
      <Route path="/lead-finder" element={<ProtectedRoute><ProspectingDashboard /></ProtectedRoute>} />
      <Route path="/lead-finder/icp" element={<ProtectedRoute><ICPManager /></ProtectedRoute>} />
      <Route path="/lead-finder/pipeline" element={<ProtectedRoute><LeadPipeline /></ProtectedRoute>} />
      <Route path="/lead-finder/leads/:leadId" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
      <Route path="/lead-finder/import" element={<ProtectedRoute><ImportLeads /></ProtectedRoute>} />
      <Route path="/lead-finder/scraping" element={<ProtectedRoute><ScrapingJobs /></ProtectedRoute>} />
      <Route path="/lead-finder/sequences" element={<ProtectedRoute><Sequences /></ProtectedRoute>} />
      <Route path="/lead-finder/sequences/new" element={<ProtectedRoute><SequenceEditor /></ProtectedRoute>} />
      <Route path="/lead-finder/sequences/:sequenceId" element={<ProtectedRoute><SequenceEditor /></ProtectedRoute>} />
      <Route path="/lead-finder/templates" element={<ProtectedRoute><MessageTemplates /></ProtectedRoute>} />
      <Route path="/lead-finder/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/lead-finder/extension" element={<ProtectedRoute><ExtensionInstall /></ProtectedRoute>} />
      <Route path="/lead-finder/admin" element={<ProtectedRoute><ProspectingAdmin /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/p/:token" element={<PublicProposal />} />
      <Route path="/p/:token/r/:recipientToken" element={<PublicProposal />} />
      <Route path="/p/:token/payment" element={<PaymentSetup />} />
      <Route path="/payment/:token" element={<PaymentSetup />} />
      <Route path="/payment/success" element={<PaymentSetup />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
